/**
 * หน้าจอปลายทางของ route ใน Desktop shell
 *
 * ทุกหน้าอ่านสิทธิ์จาก `<Outlet />` ของ shell ไม่ยิง `GET /v1/me/permissions` เอง
 * และไม่มีหน้าไหนตัดสินสิทธิ์จาก `system_roles` — ใช้ `can()` ซึ่งอ่านเฉพาะ
 * `modules` ที่เซิร์ฟเวอร์ส่งมาแล้วเท่านั้น
 */

import { useLocation, useNavigate } from 'react-router-dom';

import { ModuleNotAvailableView, NotAuthorisedView } from './AccessViews';
import { HomePage } from './HomePage';
import { BomRoutingScreen } from './bom/BomRoutingScreen';
import { CustomerScreen } from './customers/CustomerScreen';
import { useDesktopContext } from './desktopContext';
import { GoodsReceiptScreen } from './goodsReceipts/GoodsReceiptScreen';
import { KanbanSignalScreen } from './kanban/KanbanSignalScreen';
import { ReplenishmentPolicyScreen } from './kanban/ReplenishmentPolicyScreen';
import { LotTraceabilityScreen } from './lotTraceability/LotTraceabilityScreen';
import { OfflineConflictsScreen } from './offlineConflicts/OfflineConflictsScreen';
import { OutsourceOrderScreen } from './outsourcing/OutsourceOrderScreen';
import { ForecastImportScreen } from './planning/ForecastImportScreen';
import { ManualPlanScreen } from './planning/ManualPlanScreen';
import { MrpRunScreen } from './planning/MrpRunScreen';
import { ProductionPlanScreen } from './planning/ProductionPlanScreen';
import { PurchaseOrderScreen } from './procurement/PurchaseOrderScreen';
import { DeviceRegistryScreen } from './system/DeviceRegistryScreen';
import { AccountingScreen } from './accounting/AccountingScreen';
import { CostScreen } from './costing/CostScreen';
import { CostCenterScreen } from './masterData/CostCenterScreen';
import { RouteTraceScreen } from './production/RouteTraceScreen';
import { WorkCenterScreen } from './masterData/WorkCenterScreen';
import { InternalTransferScreen } from './inventory/InternalTransferScreen';
import { ProductionWorklistScreen } from './production/ProductionWorklistScreen';
import { ProductImportScreen } from './products/ProductImportScreen';
import { TagCountAdjustmentScreen } from './inventory/TagCountAdjustmentScreen';
import { OpeningStockScreen } from './inventory/OpeningStockScreen';
import { StagingScreen } from './inventory/StagingScreen';
import { InventoryLedgerScreen, StockOnHandScreen } from './inventory/StockViews';
import { PurchaseRequestScreen } from './procurement/PurchaseRequestScreen';
import { PurchaseRequisitionScreen } from './procurement/PurchaseRequisitionScreen';
import { ReceivingExceptionScreen } from './procurement/ReceivingExceptionScreen';
import { ProductMasterScreen } from './products/ProductMasterScreen';
import { OutputTagScreen } from './productionOrders/OutputTagScreen';
import { ProductionOrderScreen } from './productionOrders/ProductionOrderScreen';
import { IncomingInspectionRuleScreen } from './quality/IncomingInspectionRuleScreen';
import { NcrCaseScreen } from './quality/NcrCaseScreen';
import { ExecutiveDashboardScreen } from './reporting/ExecutiveDashboardScreen';
import { DeliveryScreen } from './sales/DeliveryScreen';
import { PackingScreen } from './sales/PackingScreen';
import { SalesOrderScreen } from './sales/SalesOrderScreen';
import { SupplierScreen } from './suppliers/SupplierScreen';
import { DepartmentsScreen } from './system/DepartmentsScreen';
import { EmployeesScreen } from './system/EmployeesScreen';
import { OauthClientsScreen } from './system/OauthClientsScreen';
import { PermissionMatrixScreen } from './system/PermissionMatrixScreen';
import { RbacAuditLogScreen } from './system/RbacAuditLogScreen';
import { SystemRolesScreen } from './system/SystemRolesScreen';
import { ToolRegistryScreen } from './tooling/ToolRegistryScreen';
import {
  ACCOUNT_PASSWORD_PATH,
  type AccountPasswordLocationState,
  type DesktopModuleRoute,
} from './routes';
import { ChangeOwnPasswordPanel } from '../lib/auth/ChangeOwnPasswordPanel';
import { can } from '../lib/permissions/client';

/**
 * รายการเมนูที่มีหน้าจอจริงในบิลด์นี้แล้ว
 *
 * อ้างด้วย `navItemId` ไม่ใช่ path เพราะ path เป็นของ `routes.ts` และเปลี่ยนได้
 * ส่วน id ของรายการเมนูคือสิ่งที่ผูกเมนู/route/หน้าจอเข้าด้วยกัน
 */
const PRODUCT_MASTER_NAV_ITEM = 'product-master';

/**
 * BOM/Routing — หน้าจอเสร็จตั้งแต่ `WO-P1-M10-011` และ**เพิ่งเปิดให้เข้าจากเมนู**
 *
 * รายการเมนูถูกกันไว้เป็น `planned` โดยตั้งใจตลอดช่วงที่ endpoint ของกลุ่มนี้ยัง
 * ถูกสร้างขนานกันอยู่ (`WO-P1-M4-001`) เพราะ `docs/SCOPE.md` ห้ามทำให้หน้าจอที่ยัง
 * ไม่มี endpoint จริงดูเหมือนธุรกรรมที่ใช้งานได้ ระหว่างนั้นเข้าถึงได้ทางเดียวจาก
 * ปุ่มบนหน้ารายละเอียดสินค้า ซึ่งเป็นที่ที่ผู้ใช้ระบุสินค้าไปแล้ว
 *
 * ตอนนี้ API ลงแล้ว รายการเมนูจึงเป็น `available` และชี้ path เดียวกันกับที่นี่
 * (หน้าจอที่เปิดจากเมนูตรง ๆ จะขึ้นสถานะ "ยังไม่ได้เลือกสินค้า" ซึ่งเป็นสถานะที่มัน
 * มีอยู่แล้วและพาผู้ใช้ไปเลือกสินค้าต่อ)
 */
const BOM_ROUTING_NAV_ITEM = 'bom-routing';

/**
 * ทะเบียนผู้ขาย — `WO-P2-M1-002` (P2-1b) และเป็นหน้าจอ **แรกของ Phase 2**
 *
 * `POST`/`GET /v1/suppliers` และ `PATCH /v1/suppliers/{supplier_id}` มี endpoint
 * จริงแล้ว (`WO-P2-M1-001`, D-083) รายการเมนูจึงถูกเปิดพร้อมกับหน้าจอนี้
 *
 * route ถูก gate ด้วย `SUPPLIER_MASTER` + **VIEW** เหมือนหน้าจอที่เปิดใช้งานแล้ว
 * ทุกตัว ส่วน CREATE และ EDIT ถูกบังคับ **ข้างใน** `SupplierScreen` ตรงที่ของมันคือ
 * ปุ่ม "เพิ่มผู้ขาย" และปุ่ม "แก้ไข" รายแถว — ซึ่งตรงกับด่านจริงฝั่งเซิร์ฟเวอร์
 * (list/get = VIEW, create = CREATE, patch = EDIT)
 *
 * `SUPPLIER_MASTER` เป็นโมดูล **ธุรกิจ** ปกติ ไม่ใช่กลุ่ม System Administration —
 * ADMIN จึงถูกปฏิเสธที่ด่านนี้เหมือนหน้าจอธุรกิจตัวอื่นทุกตัว (D-060)
 */
/**
 * Tool/Die Master — `WO-P2-M11-010` (P2-11d, D-108 ข้อ 4 และ 5) — **หน้าจอสุดท้าย
 * ของ Phase 2**
 *
 * สิบเส้นทางของ contract มี endpoint จริงแล้ว (`WO-P2-M11-009`) รายการเมนูจึงถูกเปิด
 * พร้อมกับหน้าจอนี้
 *
 * route ถูก gate ด้วย `PRODUCT_MASTER` + **VIEW** — โมดูลเดียวกับสินค้าและ BOM/Routing
 * ไม่ใช่โมดูลใหม่ (`router.py`: "No new module code") ส่วนสิทธิ์ที่แรงกว่าถูกบังคับ
 * **ข้างใน** `ToolRegistryScreen` ตรงที่ของมันคือปุ่มแต่ละปุ่ม — CREATE ที่ปุ่ม
 * "เพิ่มเครื่องมือ" และ EDIT ที่ปุ่มแก้ไข/เพิ่มการเชื่อมโยง/บันทึกซ่อมบำรุงทั้งหมด
 * ซึ่งตรงกับด่านจริงฝั่งเซิร์ฟเวอร์ทีละเส้น
 */
const TOOL_REGISTRY_NAV_ITEM = 'tool-registry';

const SUPPLIER_MASTER_NAV_ITEM = 'supplier-master';

/**
 * ทะเบียนลูกค้า — `WO-P2-M4-003` (P2-4a) และเป็น **ฝาแฝดของทะเบียนผู้ขาย**
 *
 * ทั้งสี่เส้นของ contract มี endpoint จริงแล้ว (`WO-P2-M4-001`, D-089) รายการเมนูจึง
 * ถูกเปิดพร้อมกับหน้าจอนี้ และกลุ่ม `master-data` มีทะเบียนคู่ค้าครบทั้งสองฝั่งแล้ว
 *
 * route ถูก gate ด้วย `CUSTOMER_MASTER` + **VIEW** เหมือนหน้าจอที่เปิดใช้งานแล้ว
 * ทุกตัว ส่วน CREATE และ EDIT ถูกบังคับ **ข้างใน** `CustomerScreen` ตรงที่ของมันคือ
 * ปุ่ม "เพิ่มลูกค้า" และปุ่ม "แก้ไข" รายแถว — ซึ่งตรงกับด่านจริงฝั่งเซิร์ฟเวอร์
 * (list/get = VIEW, create = CREATE, patch = EDIT)
 *
 * `CUSTOMER_MASTER` เป็นโมดูล **ธุรกิจ** ปกติ ไม่ใช่กลุ่ม System Administration —
 * ADMIN จึงถูกปฏิเสธที่ด่านนี้เหมือนหน้าจอธุรกิจตัวอื่นทุกตัว (D-060)
 *
 * **ไม่มีเส้นทางลบ** ทั้งใน contract และในหน้าจอ — เลิกค้าขายคือ `PATCH` ที่มี
 * ผู้กระทำและมีแถว audit
 */
const CUSTOMER_MASTER_NAV_ITEM = 'customer-master';

/**
 * ใบขอซื้อ — `WO-P2-M2-003` (P2-2a, D-084) และเป็น **กลุ่มเมนูใหม่กลุ่มแรกของ Phase 2**
 *
 * ทั้งหกเส้นของ contract มี endpoint จริงแล้ว (`WO-P2-M2-001`) รายการเมนูจึงถูกเปิด
 * พร้อมกับหน้าจอนี้ ส่วนใบสั่งซื้อ (P2-2b) จะเข้ามาอยู่ในกลุ่มเดียวกันใน Work Order
 * ถัดไป
 *
 * route ถูก gate ด้วย `PROCUREMENT` + **VIEW** เหมือนหน้าจอที่เปิดใช้งานแล้วทุกตัว
 * ส่วนสิทธิ์ที่แรงกว่าถูกบังคับ **ข้างใน** `PurchaseRequisitionScreen` ตรงที่ของมัน
 * คือปุ่มแต่ละปุ่ม — CREATE ที่ปุ่ม "เปิดใบขอซื้อ", EDIT ที่ปุ่มส่งอนุมัติ/ยกเลิก และ
 * APPROVE ที่ปุ่มอนุมัติ ซึ่งตรงกับด่านจริงฝั่งเซิร์ฟเวอร์ทีละเส้น (list/get = VIEW,
 * create = CREATE, submit/cancel = EDIT, approve = APPROVE)
 *
 * `PROCUREMENT` เป็นโมดูล **ธุรกิจ** ปกติ ไม่ใช่กลุ่ม System Administration — ADMIN
 * จึงถูกปฏิเสธที่ด่านนี้เหมือนหน้าจอธุรกิจตัวอื่นทุกตัว (D-060)
 */
const PURCHASE_REQUISITIONS_NAV_ITEM = 'purchase-requisitions';
const PURCHASE_REQUESTS_NAV_ITEM = 'purchase-requests';

/**
 * ใบสั่งซื้อ — `WO-P2-M2-004` (P2-2b, D-085) และเป็นหน้าจอที่ **ปิด flow จัดซื้อ**
 *
 * ทั้งเจ็ด operation ของ contract มี endpoint จริงแล้ว (`WO-P2-M2-002`) รายการเมนูจึง
 * ถูกเปิดพร้อมกับหน้าจอนี้ และกลุ่ม `procurement` มีสองรายการแล้ว
 *
 * route ถูก gate ด้วย `PROCUREMENT` + **VIEW** — โมดูลเดียวกับใบขอซื้อ ไม่ใช่โมดูลใหม่
 * ส่วนสิทธิ์ที่แรงกว่าถูกบังคับ **ข้างใน** `PurchaseOrderScreen` ตรงที่ของมันคือปุ่ม
 * แต่ละปุ่ม — CREATE ที่ปุ่ม "ออกใบสั่งซื้อ", EDIT ที่ปุ่มส่ง/ยืนยัน/ยกเลิก และ APPROVE
 * ที่ปุ่มอนุมัติ ซึ่งตรงกับด่านจริงฝั่งเซิร์ฟเวอร์ทีละเส้น (list/get = VIEW,
 * create = CREATE, send/acknowledge/cancel = EDIT, approve = APPROVE)
 */
const PURCHASE_ORDERS_NAV_ITEM = 'purchase-orders';

/**
 * ข้อยกเว้นการรับของ — `WO-P2-M14-011` (D-168 B6) และเป็น **รายการที่สามของกลุ่มเมนู
 * "จัดซื้อ"**
 *
 * `GET /v1/receiving-exceptions`/`/summary` มี endpoint จริงแล้ว (D-167, D-168)
 * รายการเมนูจึงถูกเปิดพร้อมกับหน้าจอนี้
 *
 * route ถูก gate ด้วย `PROCUREMENT` + **VIEW** — โมดูลเดียวกับใบขอซื้อ/ใบสั่งซื้อ
 * ไม่ใช่โมดูลใหม่ ส่วนปุ่ม "อนุมัติ" ข้างในหน้าจอต้องการ `INVENTORY` + **APPROVE**
 * ซึ่งเป็น grant เดิมของ `POST /v1/receiving-discrepancies/{id}/approve` (D-091)
 * ถูกบังคับ **ข้างใน** `ReceivingExceptionScreen` ตรงที่ของมันคือปุ่ม "อนุมัติ"
 * รายแถว — ตรงกับด่านจริงฝั่งเซิร์ฟเวอร์ (list/summary = PROCUREMENT + VIEW,
 * approve = INVENTORY + APPROVE)
 */
const RECEIVING_EXCEPTIONS_NAV_ITEM = 'receiving-exceptions';

/**
 * ใบสั่งขาย — `WO-P2-M4-004` (P2-4b, D-090) และเป็น **รายการแรกของกลุ่มเมนู "ขาย"**
 *
 * ทั้งห้า operation ของ contract มี endpoint จริงแล้ว (`WO-P2-M4-002`) รายการเมนูจึง
 * ถูกเปิดพร้อมกับหน้าจอนี้ กลุ่ม `sales` เป็นกลุ่มใหม่ที่จะโตต่อด้วย Forecast/MPS
 * (P2-5) และ Packing/Delivery (P2-9) ซึ่งเป็นงานของแผนกเดียวกัน
 *
 * route ถูก gate ด้วย `SALES` + **VIEW** เหมือนหน้าจอที่เปิดใช้งานแล้วทุกตัว ส่วน
 * สิทธิ์ที่แรงกว่าถูกบังคับ **ข้างใน** `SalesOrderScreen` ตรงที่ของมันคือปุ่มแต่ละ
 * ปุ่ม — CREATE ที่ปุ่ม "รับใบสั่งซื้อของลูกค้า" และ EDIT ที่ปุ่มยืนยัน/ยกเลิก ซึ่ง
 * ตรงกับด่านจริงฝั่งเซิร์ฟเวอร์ทีละเส้น (list/get = VIEW, create = CREATE,
 * confirm/cancel = EDIT)
 *
 * **`confirm` เป็น EDIT ไม่ใช่ APPROVE** ต่างจากใบขอซื้อ/ใบสั่งซื้อ — contract
 * ประกาศไว้อย่างนั้น แต่กฎแบ่งแยกหน้าที่ยังเป็นข้อเดียวกัน (D-010): ผู้ยืนยันต้อง
 * ไม่ใช่ผู้เปิดใบ ซึ่งเซิร์ฟเวอร์ตอบเป็น `422` ไม่ใช่ `403`
 *
 * `SALES` เป็นโมดูล **ธุรกิจ** ปกติ ไม่ใช่กลุ่ม System Administration — ADMIN จึงถูก
 * ปฏิเสธที่ด่านนี้เหมือนหน้าจอธุรกิจตัวอื่นทุกตัว (D-060)
 */
const SALES_ORDERS_NAV_ITEM = 'sales-orders';

/**
 * Packing — `WO-P2-M9-006` (P2-9f, D-105) และเป็น **รายการที่สองของกลุ่มเมนู "ขาย"**
 *
 * ห้า operation ที่ router จริง serve (`GET`/`POST /v1/packing-units`, `GET`
 * หนึ่งใบ, `confirm`, `unpack`) มี endpoint จริงแล้ว (`WO-P2-M9-002`) รายการเมนู
 * จึงถูกเปิดพร้อมกับหน้าจอนี้
 *
 * ⚠️ `docs/API_CONTRACT.md` เอ่ยถึง `POST /v1/packing-units/{id}/lots` แต่
 * router จริงไม่ได้ลงทะเบียน route นั้น — `PackingScreen` และ `lib/packing/**`
 * สร้างเฉพาะห้าเส้นที่ backend serve จริง (ดูหมายเหตุยาวใน `lib/packing/types.ts`)
 *
 * route ถูก gate ด้วย `SALES` + **VIEW** เหมือนใบสั่งขาย ส่วนสิทธิ์ที่แรงกว่าถูก
 * บังคับ **ข้างใน** `PackingScreen` ตรงที่ของมันคือปุ่มแต่ละปุ่ม — CREATE ที่ปุ่ม
 * "เปิดกล่องใหม่" และ EDIT ที่ปุ่มยืนยัน/เปิดกล่องใหม่ ซึ่งตรงกับด่านจริงฝั่ง
 * เซิร์ฟเวอร์ทีละเส้น (list/get = VIEW, create = CREATE, confirm/unpack = EDIT)
 *
 * **`unpack` เป็น EDIT ไม่ใช่ APPROVE** — การกลับ Delivery ต่างหากที่เป็น APPROVE
 * (`WO-P2-M9-007`, ไม่ได้อยู่บนหน้าจอนี้)
 */
const PACKING_UNITS_NAV_ITEM = 'packing-units';

/**
 * Delivery — `WO-P2-M9-007` (P2-9g, D-105) และเป็น **รายการที่สามของกลุ่มเมนู "ขาย"**
 *
 * ห้า operation ของ contract (`POST /v1/packing-units/{id}/load`,
 * `POST /v1/deliveries`, `GET /v1/deliveries`, `GET /v1/deliveries/{id}`,
 * `POST /v1/deliveries/{id}/reverse`) ถูกตรวจกับ router จริงแล้วก่อนเขียนหน้าจอนี้
 * — ตรงกับ `docs/API_CONTRACT.md` ทุกเส้น ไม่มี discrepancy แบบที่ `WO-P2-M9-006`
 * เจอ
 *
 * route ถูก gate ด้วย `SALES` + **VIEW** เหมือน Packing ส่วนสิทธิ์ที่แรงกว่าถูก
 * บังคับ **ข้างใน** `DeliveryScreen` ตรงที่ของมันคือปุ่มแต่ละปุ่ม — **EDIT** ที่ปุ่ม
 * "โหลดขึ้นรถ", **CREATE** ที่ปุ่ม "ส่งออกจากประตู" และ **APPROVE** ที่ปุ่ม
 * "ถอนการขาย" ซึ่งตรงกับด่านจริงฝั่งเซิร์ฟเวอร์ทีละเส้น (list/get = VIEW,
 * load = EDIT, dispatch = CREATE, reverse = APPROVE)
 *
 * **THE PERMISSION HEADLINE (D-105 ข้อ 6)**: dispatch กับ reverse ต้องการคนละ
 * สิทธิ์กันโดยตั้งใจ — คนที่ส่งของออกไปได้ (CREATE) ไม่ได้แปลว่าถอนการขายได้
 * (APPROVE)
 */
const DELIVERIES_NAV_ITEM = 'deliveries';

/**
 * Forecast ลูกค้า — `WO-P2-M5-003` (P2-5a, D-093) และเป็น **รายการเดียวของกลุ่มเมนู
 * "วางแผน"** ซึ่งเป็นกลุ่มใหม่
 *
 * ทั้งสี่ operation ของ contract มี endpoint จริงแล้ว (`WO-P2-M5-001`) รายการเมนูจึง
 * ถูกเปิดพร้อมกับหน้าจอนี้ กลุ่ม `planning` จะโตต่อด้วย Net Forecast (P2-5b), MPS
 * (P2-5c) และ Capacity/MRP (P2-6) ซึ่ง D-093 ระบุไว้แล้วว่าอยู่ในโมดูลเดียวกันนี้
 *
 * route ถูก gate ด้วย `PLANNING` + **VIEW** เหมือนหน้าจอที่เปิดใช้งานแล้วทุกตัว ส่วน
 * สิทธิ์ที่แรงกว่าถูกบังคับ **ข้างใน** `ForecastImportScreen` ตรงที่ของมันคือปุ่มแต่
 * ละปุ่ม — CREATE ที่ปุ่ม "นำ Forecast เข้าระบบ" และ **APPROVE** ที่ปุ่ม "ประกาศใช้"
 * รายแถว ซึ่งตรงกับด่านจริงฝั่งเซิร์ฟเวอร์ทีละเส้น (list/get = VIEW,
 * create = CREATE, publish = APPROVE)
 *
 * **`publish` เป็น APPROVE ไม่ใช่ EDIT** ต่างจาก `confirm` ของใบสั่งขาย — flow ข้อ
 * 1.4 ระบุว่า "ผู้รับผิดชอบตรวจและ Publish" การนำไฟล์เข้ากับการรับรองว่าแผนนี้ใช้ได้
 * เป็นคนละการกระทำ และ contract แยกสิทธิ์ของสองอย่างนี้ไว้ตั้งแต่ต้น
 *
 * `PLANNING` เป็นโมดูล **ธุรกิจ** ปกติ ไม่ใช่กลุ่ม System Administration — ADMIN จึง
 * ถูกปฏิเสธที่ด่านนี้เหมือนหน้าจอธุรกิจตัวอื่นทุกตัว (D-060)
 */
const FORECAST_IMPORTS_NAV_ITEM = 'forecast-imports';

/**
 * รัน MRP — `WO-P2-M6-006` (P2-6e, D-100/D-101) และเป็น **รายการที่สองของกลุ่มเมนู
 * "วางแผน"**
 *
 * ทั้งสี่ operation ของ router จริงมี endpoint จริงแล้ว (`WO-P2-M6-003`..`005`)
 * รายการเมนูจึงถูกเปิดพร้อมกับหน้าจอนี้ — ปลายทางสุดท้ายของสาย Forecast → MPS → MRP
 * ที่ D-093 วางไว้ในโมดูล `PLANNING` เดียวกัน (MPS เองยังไม่มีหน้าจอ)
 *
 * route ถูก gate ด้วย `PLANNING` + **VIEW** เหมือนหน้าจอที่เปิดใช้งานแล้วทุกตัว ส่วน
 * สิทธิ์ที่แรงกว่าถูกบังคับ **ข้างใน** `MrpRunScreen` ตรงที่ของมันคือปุ่มแต่ละปุ่ม —
 * **CREATE** ที่ปุ่ม "เริ่มรัน MRP" และ **CREATE** ที่ปุ่ม "ปล่อยเป็นเอกสารจริง" รายรัน
 * (ทั้งสองถือ grant เดียวกันตามที่ router จริงประกาศ)
 *
 * **การปล่อยไม่ต้องการ APPROVE** ต่างจาก `publish` ของ Forecast — D-101: ทุกเอกสาร
 * ที่มันสร้างเป็น `DRAFT` เสมอ และคู่ตาที่สอง (D-010) สิงอยู่ที่ตัวเอกสารนั้น ไม่ใช่
 * ที่การปล่อย
 *
 * `PLANNING` เป็นโมดูล **ธุรกิจ** ปกติ ไม่ใช่กลุ่ม System Administration — ADMIN จึง
 * ถูกปฏิเสธที่ด่านนี้เหมือนหน้าจอธุรกิจตัวอื่นทุกตัว (D-060)
 */
const MRP_RUNS_NAV_ITEM = 'mrp-runs';

/**
 * แผนการผลิต — `WO-P2-M5-006` (D-127) — **รายการที่สามของกลุ่มเมนู "วางแผน"**
 *
 * `GET /v1/mps-versions` + `GET /v1/mps-attainment` มี endpoint จริงอยู่แล้วทั้งคู่
 * (D-110 ข้อ 1) route ถูก gate ด้วย `PLANNING` + VIEW เดียวกับ Forecast/MRP
 */
const PRODUCTION_PLAN_NAV_ITEM = 'production-plan';

/**
 * วางแผนผลิตแบบ Manual — `WO-P2-M5-010` (D-129) — ไม่อยู่ใน Sidebar อีกแล้วตั้งแต่
 * `WO-P2-M5-014` (D-131 แทนที่ D-130) แต่ route/guard/หน้าจอนี้ไม่เปลี่ยน
 *
 * `POST /v1/mps-versions` มี endpoint จริงอยู่แล้ว (D-096) route ถูก gate ด้วย
 * `PLANNING` + VIEW เดียวกับรายการอื่นของกลุ่มนี้ ส่วนปุ่ม "สร้างแผนใหม่" บนหน้าจอเอง
 * ต้องการ CREATE เพิ่ม
 */
const MANUAL_PLAN_NAV_ITEM = 'manual-plan';

/**
 * ใบสั่งผลิต — `WO-P1-M10-014`
 *
 * `create`/`list`/`get`/`release` มี endpoint จริงแล้ว (`WO-P1-M5-001`) ส่วน
 * `complete`/`close`/`cancel` ยังไม่มี และหน้าจอไม่ยิงไปหามัน — ดู
 * `productionOrders/ProductionOrderPendingWork.tsx`
 */
const PRODUCTION_ORDERS_NAV_ITEM = 'production-orders';

/**
 * ป้าย Kanban การผลิต — `WO-P2-M16-001` (D-169, D-170)
 *
 * `POST`/`GET /v1/output-tags` และ `reprint` มี endpoint จริงแล้ว (`0a8f9b6`)
 * รายการเมนูจึงถูกเปิดพร้อมกับหน้าจอนี้ — อยู่หลังใบสั่งผลิตทันทีเพราะป้ายเป็น
 * เอกสารของใบสั่งผลิตที่ปล่อยแล้ว
 */
const OUTPUT_TAGS_NAV_ITEM = 'output-tags';

/**
 * รับเข้าสต๊อก — `WO-P1-M10-015`
 *
 * `list`/`get`/`post` มี endpoint จริงแล้ว (`WO-P1-M4B-001`) รายการเมนูจึงถูกปลด
 * จาก `planned` พร้อมกับหน้าจอนี้ ส่วน `POST /v1/lots/{lot_id}/quality-decision`
 * ยังเป็นงานของ `WO-P1-M4B-002` และหน้าจอไม่ยิงไปหามัน — ของที่รับเข้าทุกกอง
 * แสดงเป็น `QUALITY_HOLD` และบอกตรง ๆ ว่ายังเบิกไปใช้ไม่ได้ (D-059)
 *
 * route นี้ถูก gate ด้วย `INVENTORY` + **VIEW** เหมือนหน้าจอที่เปิดใช้งานแล้วทุกตัว
 * ส่วน `INVENTORY` + CREATE ถูกบังคับ **ข้างใน** `GoodsReceiptScreen` ตรงที่เดียวคือ
 * ฟอร์มรับของเข้า — ด่านของ route ตอบว่า "เปิดหน้าจอนี้ได้ไหม" ไม่ใช่ "ทำอะไรได้
 * บ้างบนหน้าจอนี้" การยกด่านหลังขึ้นมาไว้ที่ route จะซ่อนรายการที่
 * `GET /v1/goods-receipts` (VIEW ฝั่งเซิร์ฟเวอร์) ยินดีจะตอบ และจะตัด AUDITOR
 * ซึ่งเป็น VIEW-only โดยการออกแบบ (D-009) ออกจากสมุดรับเข้าที่เขามีหน้าที่ตรวจ
 */
const GOODS_RECEIPT_NAV_ITEM = 'goods-receipt';
const STOCK_ON_HAND_NAV_ITEM = 'stock-on-hand';
const INITIAL_STOCK_IMPORT_NAV_ITEM = 'initial-stock-import';
const PRODUCT_STAGING_NAV_ITEM = 'product-staging';
const INVENTORY_LEDGER_NAV_ITEM = 'inventory-ledger';

/**
 * กฎตรวจรับเข้า — `WO-P2-M3-003` (P2-3a, D-086)
 *
 * ทั้งสี่เส้นของ contract มี endpoint จริงแล้ว (`WO-P2-M3-001`) รายการเมนูจึงถูกเปิด
 * พร้อมกับหน้าจอนี้ และกลุ่ม `inventory` มีรายการที่ใช้งานได้จริงสองรายการแล้ว
 *
 * เป็นหน้าจอ **คู่กัน**กับการรับเข้าสต๊อกด้านบน ไม่ใช่หน้าจอตั้งค่าที่แยกขาด: ของที่
 * รับเข้าใหม่อยู่ในสถานะกักรอตรวจเสมอ **เว้นแต่**คู่ (สินค้า, ผู้ขาย) นั้นมีกฎที่บอก
 * ว่าไม่ต้องตรวจ — หน้านี้คือที่เดียวที่กฎนั้นถูกตั้งและถูกยกเลิก
 *
 * route ถูก gate ด้วย **`INVENTORY` + VIEW** — โมดูลเดียวกับการรับเข้าสต๊อก ไม่ใช่
 * โมดูลใหม่ชื่อ `QUALITY` (D-086) ส่วน CREATE และ EDIT ถูกบังคับ **ข้างใน**
 * `IncomingInspectionRuleScreen` ตรงที่ของมันคือปุ่ม "ตั้งกฎใหม่" และปุ่ม "แก้ไข"
 * รายแถว ซึ่งตรงกับด่านจริงฝั่งเซิร์ฟเวอร์ (list/get = VIEW, create = CREATE,
 * patch = EDIT)
 *
 * **ไม่มีเส้นทางลบ** ทั้งใน contract และในหน้าจอ — ยกเลิกการตรวจคือ `PATCH` ที่มี
 * ผู้กระทำและมีแถว audit
 */
const INCOMING_INSPECTION_RULES_NAV_ITEM = 'incoming-inspection-rules';

/**
 * เคส NCR — `WO-P2-M11-008` (P2-11b, D-108 ข้อ 2 และ 3)
 *
 * ทั้งห้าเส้นของ contract มี endpoint จริงแล้ว (`WO-P2-M11-007`) รายการเมนูจึงถูก
 * เปิดพร้อมกับหน้าจอนี้ และกลุ่ม `inventory` มีรายการที่ใช้งานได้จริงสามรายการแล้ว
 *
 * route ถูก gate ด้วย **`INVENTORY` + VIEW** — โมดูลเดียวกับกฎตรวจรับเข้าและการรับ
 * เข้าสต๊อก ไม่ใช่โมดูลใหม่ชื่อ `QUALITY` ส่วนสิทธิ์ที่แรงกว่าถูกบังคับ **ข้างใน**
 * `NcrCaseScreen` ตรงที่ของมันคือปุ่มแต่ละปุ่ม — CREATE ที่ปุ่ม "เปิดเคสใหม่", EDIT
 * ที่ปุ่ม "สอบสวน" และ APPROVE ที่ปุ่ม "ปิดเคส" ซึ่งตรงกับด่านจริงฝั่งเซิร์ฟเวอร์ทีละ
 * เส้น (list/get = VIEW, create = CREATE, investigate = EDIT, close = APPROVE)
 *
 * **`close` เป็น APPROVE ไม่ใช่ EDIT** — มันคือการกระทำเดียวที่ย้ายสถานะคุณภาพของ
 * ล็อตจริง (D-108 ข้อ 2) จึงถือ authority เดียวกับที่ D-059 ให้กับการตัดสินใจคุณภาพ
 * ของล็อตในโมดูลเดียวกันนี้
 */
const NCR_CASES_NAV_ITEM = 'ncr-cases';

/**
 * ตามสอบ Lot — `WO-P1-M10-020`
 *
 * `GET /v1/lots/{lot_id}/genealogy/backward` และ `/forward` มี endpoint จริงแล้ว
 * (`WO-P1-M8-001`, D-073) รายการเมนูจึงถูกเปิดพร้อมกับหน้าจอนี้ ส่วนสองรายการเดิม
 * ของกลุ่มนี้ (`genealogy-backward`/`genealogy-forward`) ยังเป็น `planned` ตามเดิม
 * — ดูเหตุผลใน `navigation.ts`
 *
 * route ถูก gate ด้วย **`INVENTORY` + VIEW** ไม่ใช่ `LOT_TRACEABILITY`: D-073 ระบุ
 * ว่า Phase 1 ไม่มีโมดูลนั้น และ endpoint ทั้งสองเส้นก็ถูก gate ด้วย
 * `require_permission("INVENTORY", "VIEW")` ฝั่งเซิร์ฟเวอร์
 *
 * **ไม่มีการ์ดตัวที่สองข้างในหน้าจอ** เพราะไม่มีปุ่มใดให้กัน — หน้าจอนี้อ่านอย่างเดียว
 * และ edge ของ `lot_genealogy` ถูกเขียนโดยธุรกรรม Scan-Out เท่านั้น (D-065) จึงไม่
 * รับ `permissions` เข้าไป ด้วยเหตุผลเดียวกับ `RbacAuditLogScreen`
 */
const LOT_TRACEABILITY_NAV_ITEM = 'lot-traceability';

/**
 * ข้อขัดแย้งจากการซิงก์เครื่องสแกน — `WO-P1-M10-021`
 *
 * `GET /v1/offline-sync/conflicts` และ `POST .../{id}/resolve` มี endpoint จริงแล้ว
 * (`WO-P1-M11-005`, D-077) รายการเมนูจึงถูกเปิดพร้อมกับหน้าจอนี้ ส่วนรายการ
 * `shop-floor-scan` ยังเป็น `planned` ตามเดิม เพราะการสแกนจริงอยู่บนเครื่อง Android
 *
 * route ถูก gate ด้วย **`SHOP_FLOOR` + VIEW** เหมือนหน้าจอที่เปิดใช้งานแล้วทุกตัว
 * ส่วน `SHOP_FLOOR` + **APPROVE** ถูกบังคับ **ข้างใน** `OfflineConflictsScreen` ตรงที่
 * เดียวคือปุ่มปิดรายการ — ผู้ที่มีแต่ VIEW เห็นคิวครบทุกแถวแต่ไม่มีปุ่ม ซึ่งตรงกับ
 * ด่านจริงฝั่งเซิร์ฟเวอร์ (list = VIEW, resolve = APPROVE)
 */
const OFFLINE_CONFLICTS_NAV_ITEM = 'offline-sync-conflicts';

/**
 * ตั้งค่า Kanban (Replenishment Policy) — `WO-P2-M7-006` (P2-7f, D-102) และเป็น
 * **หนึ่งในสองรายการใหม่ของกลุ่ม "การผลิต"** ที่แทนที่รายการ `phase-2` เดิม
 * ("เติมเต็มแบบ Pull / Kanban")
 *
 * ทั้งแปด route ของ `KANBAN_CONFIG` มี endpoint จริงแล้ว
 * (`WO-P2-M7-001`..`WO-P2-M7-005`) รายการเมนูจึงถูกเปิดพร้อมกับหน้าจอนี้
 *
 * route ถูก gate ด้วย `KANBAN_CONFIG` + **VIEW** เหมือนหน้าจอที่เปิดใช้งานแล้วทุกตัว
 * ส่วนสิทธิ์ที่แรงกว่าถูกบังคับ **ข้างใน** `ReplenishmentPolicyScreen` ตรงที่ของมันคือ
 * ปุ่มแต่ละปุ่ม — CREATE ที่ปุ่ม "เขียน policy ใหม่"/"ตัด control cycle"/"ออกการ์ด"
 * และ APPROVE ที่ปุ่ม "อนุมัติ" ซึ่งตรงกับด่านจริงฝั่งเซิร์ฟเวอร์ทีละเส้น
 *
 * **`KANBAN_CONFIG` ไม่ใช่ `KANBAN_EXECUTION`** — สองโมดูลนี้ไม่มีวันปรากฏบนหน้าจอ
 * เดียวกันเลย (D-102 ข้อ 1, ดู `KANBAN_SIGNALS_NAV_ITEM` ด้านล่าง) `KANBAN_CONFIG`
 * เป็นโมดูล **ธุรกิจ** ปกติ ไม่ใช่กลุ่ม System Administration — ADMIN จึงถูกปฏิเสธ
 * ที่ด่านนี้เหมือนหน้าจอธุรกิจตัวอื่นทุกตัว (D-060)
 */
const KANBAN_REPLENISHMENT_POLICY_NAV_ITEM = 'kanban-replenishment-policy';

/**
 * บอร์ด Kanban (Signal) — `WO-P2-M7-006` (P2-7f, D-102) และเป็น **รายการที่สองของ
 * คู่ที่ D-102 แยกไว้** — ดูหมายเหตุยาวที่ `KANBAN_REPLENISHMENT_POLICY_NAV_ITEM`
 *
 * ทั้งหกเส้นทางของ `KANBAN_EXECUTION` มี endpoint จริงแล้ว
 * (`WO-P2-M7-002`, `WO-P2-M7-005`) รายการเมนูจึงถูกเปิดพร้อมกับหน้าจอนี้
 *
 * route ถูก gate ด้วย `KANBAN_EXECUTION` + **VIEW** ส่วนสิทธิ์ที่แรงกว่าถูกบังคับ
 * **ข้างใน** `KanbanSignalScreen` ตรงที่ของมันคือปุ่มแต่ละปุ่ม — **CREATE** ที่ปุ่ม
 * "สแกนการ์ดเบิก" (`empty`) และ **EDIT** ที่ปุ่ม acknowledge/start-replenishment/
 * replenish/cancel ทั้งสี่ ซึ่งตรงกับด่านจริงฝั่งเซิร์ฟเวอร์ทีละเส้น
 *
 * **ไม่มีปุ่มใดใช้ `KANBAN_CONFIG`** — การออกการ์ดและตัด control cycle เป็นของ
 * `ReplenishmentPolicyScreen` เท่านั้น (D-102 ข้อ 1: จำนวนการ์ดที่หมุนเวียนคือกำลัง
 * การผลิต ซึ่งเป็นสิ่งที่ operator หน้าไลน์ต้องแตะไม่ได้)
 */
const KANBAN_SIGNALS_NAV_ITEM = 'kanban-signals';

/**
 * ตารางสิทธิ์ตามแผนก — `WO-P1-M10-016`
 *
 * `GET /admin/departments`, `GET /admin/permissions` และ `POST /admin/permissions`
 * มี endpoint จริงแล้ว (`WO-P1-M2-011`) รายการเมนูจึงถูกปลดจาก `planned` พร้อมกับ
 * หน้าจอนี้
 *
 * route นี้ถูก gate ด้วย `RBAC_ADMIN` + **VIEW** เหมือนหน้าจอที่เปิดใช้งานแล้วทุกตัว
 * ส่วน `RBAC_ADMIN` + EDIT ถูกบังคับ **ข้างใน** `PermissionMatrixScreen` ตรงที่เดียว
 * คือปุ่มบันทึกและช่องติ๊ก — และหลัง D-060 มีเพียง system role `ADMIN` ที่ถือ
 * `RBAC_ADMIN` ได้เลย ผู้ที่ไม่ใช่ ADMIN จึงถูกปฏิเสธตั้งแต่ด่าน route อยู่แล้ว
 */
const ADMIN_PERMISSIONS_NAV_ITEM = 'admin-permissions';

/**
 * บทบาทระบบ และบันทึกการเปลี่ยนสิทธิ์ — `WO-P1-M10-017`
 *
 * `GET`/`POST /admin/roles` และ `GET /admin/audit-log` มี endpoint จริงแล้ว
 * (`WO-P1-M2-012`) สองรายการเมนูนี้จึงถูกปลดจาก `planned` พร้อมกับหน้าจอของมัน
 *
 * ทั้งสอง route ถูก gate ด้วย `RBAC_ADMIN` + **VIEW** ตามกติกาเดียวกับทุกหน้าจอที่
 * เปิดแล้ว ส่วนการ **มอบ/ถอดบทบาท** ถูกกันด้วย EDIT ข้างใน `SystemRolesScreen`
 * ตรงที่ของมัน — หน้าจอ audit ไม่มีการ์ดที่สอง เพราะมันอ่านอย่างเดียวและไม่มี
 * เส้นทางเขียนให้กัน
 */
const ADMIN_ROLES_NAV_ITEM = 'admin-roles';
const ADMIN_AUDIT_LOG_NAV_ITEM = 'admin-audit-log';
const DEVICE_REGISTRY_NAV_ITEM = 'device-registry';

/**
 * แผนก และ พนักงาน — เดิมเป็นหน้าจอเดียว (`WO-P1-M10-018`) แยกเป็นสองหน้าที่
 * `WO-P2-UI-001`
 *
 * เส้นทางของ contract ไม่เปลี่ยนแม้แต่เส้นเดียว: `POST /admin/departments` และ
 * `POST /admin/employees` (`WO-P1-M2-012`) กับ sub-resource สองเส้นของพนักงาน
 * (`WO-P1-M2-013`) ยังเป็นชุดเดิม เพียงถูกแบ่งกันถือระหว่างสองหน้าจอ —
 * `DepartmentsScreen` ถือการอ่าน/สร้างแผนก ส่วน `EmployeesScreen` ถือการอ่าน/สร้าง
 * พนักงานและการผูกแผนก/โรงงานให้เขา
 *
 * ทั้งสอง route ถูก gate ด้วย `RBAC_ADMIN` + **VIEW** เหมือนทุกหน้าจอที่เปิดแล้ว
 * ส่วนการ **สร้างและผูก** ถูกกันด้วย EDIT ข้างในแต่ละหน้าจอ ตรงที่ของมัน
 */
const ADMIN_DEPARTMENTS_NAV_ITEM = 'admin-departments';
const ADMIN_EMPLOYEES_NAV_ITEM = 'admin-employees';

/**
 * Executive Dashboard — `WO-P2-M10-005` (P2-10d, D-106) และเป็น **รายการเดียวของ
 * กลุ่มเมนู "รายงานผู้บริหาร"** ซึ่งเป็นกลุ่มใหม่
 *
 * `GET /v1/executive-summary` มี endpoint จริงแล้ว (`WO-P2-M10-004`) รายการเมนูจึง
 * ถูกเปิดพร้อมกับหน้าจอนี้
 *
 * route ถูก gate ด้วย `EXECUTIVE_DASHBOARD` + **VIEW** เหมือนหน้าจอที่เปิดใช้งาน
 * แล้วทุกตัว — และเป็น**สิทธิ์เดียวที่เคยมี**ในกลุ่มนี้: ตารางที่หน้าจอนี้อ่านไม่มี
 * เส้นทางเขียนเลย (D-106 ข้อ 5) จึงไม่มีปุ่มใดให้ CREATE/EDIT/APPROVE กันเพิ่มข้างใน
 * `ExecutiveDashboardScreen` ต่างจากทุกหน้าจอ "ธุรกิจ" อื่นในไฟล์นี้
 *
 * **ไม่รับ `permissions`** — เหตุผลเดียวกับ `LotTraceabilityScreen`/
 * `RbacAuditLogScreen`: ไม่มีปุ่มใดให้กันข้างในเลย ด่านของ route (VIEW) ที่ผ่านมา
 * แล้วก่อนถึงบรรทัดนี้คือด่านเดียวที่หน้าจอนี้ต้องการ
 *
 * `EXECUTIVE_DASHBOARD` เป็นโมดูล **ธุรกิจ** ปกติ ไม่ใช่กลุ่ม System Administration
 * — ADMIN จึงถูกปฏิเสธที่ด่านนี้เหมือนหน้าจอธุรกิจตัวอื่นทุกตัว (D-060)
 */
const EXECUTIVE_DASHBOARD_NAV_ITEM = 'executive-dashboard';

/**
 * ใบสั่งจ้างผลิตภายนอก — `WO-P2-M8-007` (P2-8g, D-103) และเป็น **รายการเดียวของ
 * กลุ่มเมนู "จ้างผลิตภายนอก"** ซึ่งเป็นกลุ่มใหม่ — หน้าจอสุดท้ายของ Phase 2 ที่ยัง
 * ไม่มีหน้าจอ
 *
 * เจ็ด operation ของ contract ที่หน้าจอนี้ใช้ (`GET`/`POST /v1/outsource-orders`,
 * `POST .../lines`, `POST .../ready`, `POST .../dispatch`, `POST .../receive`,
 * `POST /v1/outsource-return-discrepancies/{id}/approve`) มี endpoint จริงแล้ว
 * (`WO-P2-M8-002`..`WO-P2-M8-006`) รายการเมนูจึงถูกเปิดพร้อมกับหน้าจอนี้
 *
 * route ถูก gate ด้วย `OUTSOURCING` + **VIEW** เหมือนหน้าจอที่เปิดใช้งานแล้วทุกตัว
 * ส่วนสิทธิ์ที่แรงกว่าถูกบังคับ **ข้างใน** `OutsourceOrderScreen` ตรงที่ของมันคือ
 * ปุ่มแต่ละปุ่ม — CREATE ที่ปุ่ม "เปิดใบใหม่", EDIT ที่ปุ่มเพิ่มบรรทัด/ส่งออก/รับกลับ
 * และ APPROVE ที่ปุ่มยืนยันพร้อมส่งออก/อนุมัติส่วนขาด ซึ่งตรงกับด่านจริงฝั่ง
 * เซิร์ฟเวอร์ทีละเส้น (list/get = VIEW, create = CREATE, add-line/dispatch/
 * receive = EDIT, ready/approve-discrepancy = APPROVE)
 *
 * **THE NOT-A-SALE / THE SHORTAGE HEADLINES**: การส่งออกไม่ใช่การขาย (D-103 ข้อ 1)
 * และการรับกลับที่ขาดเท่านั้นที่ตัดบัญชี (D-103 ข้อ 10g) — ทั้งสองแสดงต่างจากกัน
 * ชัดเจนข้างใน `OutsourceOrderScreen`
 *
 * `OUTSOURCING` เป็นโมดูล **ธุรกิจ** ปกติ ไม่ใช่กลุ่ม System Administration — ADMIN
 * จึงถูกปฏิเสธที่ด่านนี้เหมือนหน้าจอธุรกิจตัวอื่นทุกตัว (D-060)
 */
const OUTSOURCE_ORDERS_NAV_ITEM = 'outsource-orders';

/**
 * `/` — ภาพรวมระบบ ไม่ผูกกับโมดูลธุรกิจใด จึงไม่ถูก gate ด้วย `can()`
 *
 * ไม่มีกิ่งพิเศษสำหรับ `unauthenticated` อีกแล้ว: `401` ล้าง token ทิ้งแล้ว `App`
 * พากลับไปหน้าเข้าสู่ระบบเอง (`lib/auth/authorizedFetch.ts`) ถ้าหน้านี้ถูกเรนเดอร์
 * ในสถานะนั้นชั่วขณะ มันยังเป็นหน้าที่ deny-safe ตามเดิม — ไม่มีเมนูธุรกิจ ไม่มีข้อมูล
 */
export function DesktopHomePage() {
  const { permissions, apiBaseUrl } = useDesktopContext();
  return <HomePage permissions={permissions} apiBaseUrl={apiBaseUrl} />;
}

/**
 * อ่านหน้าจอต้นทางจาก location state — ไม่เชื่อรูปร่างของค่าที่ได้มา
 *
 * state มาจากลิงก์บน header ของเราเอง แต่มันเดินทางผ่าน history ของเบราว์เซอร์
 * ซึ่งผู้ใช้/ส่วนขยายเขียนทับได้ ค่าที่อ่านไม่ได้จึงกลายเป็น "กลับหน้าแรก"
 * ไม่ใช่ crash และ**ต้องไม่วนกลับมาหน้านี้เอง** ไม่งั้นปุ่มปิดจะกดแล้วไม่ไปไหน
 */
function backTarget(state: unknown): string {
  if (typeof state !== 'object' || state === null) {
    return '/';
  }
  const { from } = state as Partial<AccountPasswordLocationState>;
  if (typeof from !== 'string' || !from.startsWith('/') || from === ACCOUNT_PASSWORD_PATH) {
    return '/';
  }
  return from;
}

/**
 * `/account/password` — เปลี่ยนรหัสผ่านของตัวเอง (เส้นทาง VOLUNTARY)
 *
 * **ไม่มี `can()` ที่นี่โดยเจตนา** ต่างจาก `DesktopModulePage` ทุกเส้น: การเปลี่ยน
 * รหัสผ่านของตัวเองไม่ได้ผูกกับโมดูลธุรกิจใดใน RBAC matrix ผู้ใช้ที่ `modules` ว่าง
 * เปล่า (หรือสิทธิ์ยังโหลดไม่เสร็จ) ต้องยังทำได้ — ถ้าเติม guard ตรงนี้ คนที่ยังไม่
 * ได้รับสิทธิ์ใด ๆ จะเปลี่ยนรหัสผ่านที่ผู้ดูแลระบบตั้งให้ไม่ได้เลย ดู
 * `desktop/routes.ts` ก่อนคิดจะเพิ่ม
 *
 * ด่านที่ใช้จริงคือด่านเดียวกับหน้าจอธุรกิจ: ไม่มี token = ไม่ถึงหน้านี้ (`App.tsx`)
 *
 * เปลี่ยนสำเร็จแล้ว **ไม่แตะ session** — D-050 ระบุว่าเซิร์ฟเวอร์เก็บ session ของ
 * ผู้เรียกไว้ ผู้ใช้จึงกลับไปทำงานต่อที่หน้าจอเดิม ไม่ใช่ถูกพากลับหน้าเข้าสู่ระบบ
 */
export function DesktopChangePasswordPage() {
  const { apiBaseUrl } = useDesktopContext();
  const { state } = useLocation();
  const navigate = useNavigate();
  const back = backTarget(state);

  return (
    <ChangeOwnPasswordPanel
      deviceKind="DESKTOP"
      apiBaseUrl={apiBaseUrl}
      onClose={() => {
        navigate(back, { replace: true });
      }}
    />
  );
}

export interface DesktopModulePageProps {
  readonly route: DesktopModuleRoute;
}

/**
 * ทุก route ที่ผูกกับโมดูล — guard ก่อน แล้วค่อยดูว่าหน้าจอมีจริงหรือยัง
 *
 * ลำดับนี้สำคัญ: ถ้าเช็ค "ยังไม่ implement" ก่อน ผู้ที่ไม่มีสิทธิ์จะได้รู้ว่าหน้าจอนี้
 * มีอยู่ในระบบและกำลังจะมา ซึ่งเป็นการรั่วข้อมูลโครงสร้างโดยไม่จำเป็น
 */
export function DesktopModulePage({ route }: DesktopModulePageProps) {
  const { permissions, apiBaseUrl } = useDesktopContext();

  if (!can(permissions, route.moduleCode, route.action)) {
    return <NotAuthorisedView permissions={permissions} route={route} />;
  }

  if (route.navItemId === PRODUCT_MASTER_NAV_ITEM) {
    return <ProductMasterScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === BOM_ROUTING_NAV_ITEM) {
    return <BomRoutingScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === TOOL_REGISTRY_NAV_ITEM) {
    return <ToolRegistryScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === SUPPLIER_MASTER_NAV_ITEM) {
    return <SupplierScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === CUSTOMER_MASTER_NAV_ITEM) {
    return <CustomerScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === PURCHASE_REQUESTS_NAV_ITEM) {
    return <PurchaseRequestScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === PURCHASE_REQUISITIONS_NAV_ITEM) {
    return <PurchaseRequisitionScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === PURCHASE_ORDERS_NAV_ITEM) {
    return <PurchaseOrderScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === RECEIVING_EXCEPTIONS_NAV_ITEM) {
    return <ReceivingExceptionScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === SALES_ORDERS_NAV_ITEM) {
    return <SalesOrderScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === PACKING_UNITS_NAV_ITEM) {
    return <PackingScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === DELIVERIES_NAV_ITEM) {
    return <DeliveryScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === FORECAST_IMPORTS_NAV_ITEM) {
    return <ForecastImportScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === MRP_RUNS_NAV_ITEM) {
    return <MrpRunScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === PRODUCTION_PLAN_NAV_ITEM) {
    return <ProductionPlanScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === MANUAL_PLAN_NAV_ITEM) {
    return <ManualPlanScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === PRODUCTION_ORDERS_NAV_ITEM) {
    return <ProductionOrderScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === OUTPUT_TAGS_NAV_ITEM) {
    return <OutputTagScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === INITIAL_STOCK_IMPORT_NAV_ITEM) {
    return <OpeningStockScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === PRODUCT_STAGING_NAV_ITEM) {
    return <StagingScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === STOCK_ON_HAND_NAV_ITEM) {
    return <StockOnHandScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === INVENTORY_LEDGER_NAV_ITEM) {
    return <InventoryLedgerScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === GOODS_RECEIPT_NAV_ITEM) {
    return <GoodsReceiptScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === INCOMING_INSPECTION_RULES_NAV_ITEM) {
    return (
      <IncomingInspectionRuleScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />
    );
  }

  if (route.navItemId === NCR_CASES_NAV_ITEM) {
    return <NcrCaseScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === LOT_TRACEABILITY_NAV_ITEM) {
    return <LotTraceabilityScreen apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === OFFLINE_CONFLICTS_NAV_ITEM) {
    return <OfflineConflictsScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === KANBAN_REPLENISHMENT_POLICY_NAV_ITEM) {
    return <ReplenishmentPolicyScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === KANBAN_SIGNALS_NAV_ITEM) {
    return <KanbanSignalScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === ADMIN_DEPARTMENTS_NAV_ITEM) {
    return <DepartmentsScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === ADMIN_EMPLOYEES_NAV_ITEM) {
    return <EmployeesScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === ADMIN_PERMISSIONS_NAV_ITEM) {
    return <PermissionMatrixScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === ADMIN_ROLES_NAV_ITEM) {
    return <SystemRolesScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === 'work-centers') {
    return <WorkCenterScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === 'cost-centers') {
    return <CostCenterScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === 'product-import') {
    return <ProductImportScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === 'internal-transfer') {
    return <InternalTransferScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === 'tag-count-adjustments') {
    return <TagCountAdjustmentScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === 'route-trace') {
    return <RouteTraceScreen apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === 'production-worklist') {
    return <ProductionWorklistScreen apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === 'accounting-documents') {
    return <AccountingScreen apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === 'order-variance') {
    return <CostScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === DEVICE_REGISTRY_NAV_ITEM) {
    return <DeviceRegistryScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === 'oauth-clients') {
    return <OauthClientsScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === ADMIN_AUDIT_LOG_NAV_ITEM) {
    // ไม่รับ `permissions` เพราะไม่มีปุ่มใดให้กัน — หน้าจอนี้อ่านอย่างเดียว
    // และด่านของมันคือ `RBAC_ADMIN` + VIEW ที่ผ่านมาแล้วเมื่อถึงบรรทัดนี้
    return <RbacAuditLogScreen apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === EXECUTIVE_DASHBOARD_NAV_ITEM) {
    // ไม่รับ `permissions` เพราะไม่มีปุ่มใดให้กัน — หน้าจอนี้อ่านอย่างเดียว
    // และด่านของมันคือ `EXECUTIVE_DASHBOARD` + VIEW ที่ผ่านมาแล้วเมื่อถึงบรรทัดนี้
    return <ExecutiveDashboardScreen apiBaseUrl={apiBaseUrl} />;
  }

  if (route.navItemId === OUTSOURCE_ORDERS_NAV_ITEM) {
    return <OutsourceOrderScreen permissions={permissions} apiBaseUrl={apiBaseUrl} />;
  }

  return <ModuleNotAvailableView route={route} />;
}
